/** @format */

import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Grid, TextField, Select, FormControl, InputLabel, MenuItem, Checkbox } from '@material-ui/core';
import { Button } from 'src/components';
// import {ArrowRight} from '@material-ui/icons';
import { Avatar } from 'src/components/header/avatar';
import useAuth from 'src/hooks/useAuth';
import { useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import {
  deleteUserProfilePicture,
  setUserData,
  updateUserInfo,
  updateUserProfilePicture,
  uploadUserProfilePicture
} from 'src/slices/users';
import { useDispatch } from 'src/store';
import Breadcrumb from 'src/components/Breadcrumb';
import { countriesMap } from 'src/utils/dataMap';
import { useSelector } from 'src/store';
import prettyBytes from 'pretty-bytes';

const salutationValues = [
  { id: 'mr' },
  { id: 'mrs' },
  { id: 'others' }
];

const PROFILE_COMPLETION_FIELDS = [
  data => data?.salutation,
  data => data?.firstName,
  data => data?.lastName,
  data => data?.company,
  data => data?.address?.street,
  data => data?.address?.postalCode,
  data => data?.address?.city,
  data => data?.address?.phoneNumber,
  data => data?.address?.faxNumber,
  data => data?.vatNumber,
  data => data?.sealNumber,
  data => data?.drsId,
  data => data?.brakeCenterId,
  data => data?.dealerId
];

const PROFILE_COMPLETION_FOCUS_FIELDS = [
  { selector: '[name="salutation"]', getter: data => data?.salutation },
  { selector: '[name="firstName"]', getter: data => data?.firstName },
  { selector: '[name="lastName"]', getter: data => data?.lastName },
  { selector: '[name="company"]', getter: data => data?.company },
  { selector: '[name="street"]', getter: data => data?.address?.street },
  { selector: '[name="postalCode"]', getter: data => data?.address?.postalCode },
  { selector: '[name="city"]', getter: data => data?.address?.city },
  { selector: '[name="phoneNumber"]', getter: data => data?.address?.phoneNumber },
  { selector: '[name="faxNumber"]', getter: data => data?.address?.faxNumber },
  { selector: '[name="vatNumber"]', getter: data => data?.vatNumber },
  { selector: '[name="sealNumber"]', getter: data => data?.sealNumber },
  { selector: '[name="drsId"]', getter: data => data?.drsId },
  { selector: '[name="brakeCenterId"]', getter: data => data?.brakeCenterId },
  { selector: '[name="dealerId"]', getter: data => data?.dealerId }
];

const isFilledField = value => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

const PersonalData = props => {
  const { user } = useAuth();
  let history = useHistory();
  const dispatch = useDispatch();
  const { error, userProfile, userLoading, userData } = useSelector(state => state.users);
  const [errorMessage, setErrorMessage] = useState({ error: error });
  const [userProfilePicture, setUserProfilePicture] = useState('');
  const { t, i18n } = useTranslation();
  const inputFileRef = useRef();
  const profileFormRef = useRef();
  const [successMessage, setSuccessMessage] = useState(null);

  const goBack = () => {
    history.push('/profile');
  };

  const scrollToForm = () => {
    const firstEmptyField = PROFILE_COMPLETION_FOCUS_FIELDS.find(field => !isFilledField(field.getter(userData)));

    profileFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });

    if (!firstEmptyField) {
      return;
    }

    window.setTimeout(() => {
      const fieldElement = profileFormRef.current?.querySelector(firstEmptyField.selector);
      fieldElement?.focus();
    }, 350);
  };

  useEffect(() => {
    if (userProfile && userProfile.picture && !userProfilePicture) {
      setUserProfilePicture(userProfile.picture);
    }
    // eslint-disable-next-line
  }, [userProfile]);

  useEffect(() => {
    if (error) {
      const errorMsg = error.error
        .replace('The mime type of the file is invalid', t('personalData.profile_errors.mime_1'))
        .replace('Allowed mime types are', t('personalData.profile_errors.mime_2'))
        .replace('An image for this user already exists.', t('personalData.profile_errors.already_exists'))
        .replace('Unable to save image.', t('personalData.profile_errors.unable_save'))
        .replace('No image uploaded.', t('personalData.profile_errors.no_image_uploaded'))
        .replace('The file was only partially uploaded.', t('personalData.profile_errors.was_partially_uploaded'))
        .replace('No file was uploaded.', t('personalData.profile_errors.no_file_uploaded'))
        .replace('The file could not be uploaded.', t('personalData.profile_errors.file_could_not_be_uploaded'));
      setErrorMessage({ ...errorMessage, avatar: errorMsg });
      setUserProfilePicture('');
      inputFileRef.current.value = null;
    }
    // eslint-disable-next-line
  }, [error]);

  const setData = e => {
    const { name, value } = e.target;
    dispatch(
      setUserData({
        ...userData,
        [name]: value
      })
    );
  };
  const setCheck = e => {
    const { name, checked } = e.target;
    dispatch(
      setUserData({
        ...userData,
        [name]: checked
      })
    );
  };
  const setAddress = e => {
    const { name, value } = e.target;
    let address = { ...userData.address, ...{ [name]: value } };
    dispatch(
      setUserData({
        ...userData,
        address
      })
    );
  };
  const saveProfileData = async () => {
    const { firstName, lastName, company, address } = userData;
    setErrorMessage({
      ...errorMessage,
      firstName: firstName ? null : t('personalData.errorMessage.firstName'),
      lastName: lastName ? null : t('personalData.errorMessage.lastName'),
      company: company ? null : t('personalData.errorMessage.company'),
      phoneNumber: address.phoneNumber ? null : t('personalData.errorMessage.phoneNumber'),
      address: address.street && address.postalCode && address.city ? null : t('personalData.errorMessage.address')
    });
    if (
      firstName &&
      lastName &&
      company &&
      address.street &&
      address.postalCode &&
      address.city &&
      address.phoneNumber
    ) {
      setErrorMessage({ error: null });
      setSuccessMessage(null);
      await dispatch(updateUserInfo(userData));
      setSuccessMessage(t('personalData.successMessage'));
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      setSuccessMessage(null);
    }
  };

  const breadcrumbData = [
    {
      title: t('profile.title'),
      link: '/profile',
      options: [
        {
          title: t('profile.title'),
          link: '/profile'
        },
        {
          title: t('personalData.title'),
          link: '/personaldata'
        },
        {
          title: t('Access.title'),
          link: '/access'
        }
      ]
    },
    {
      title: t('personalData.title')
    }
  ];

  const onChangeProFilePicture = async e => {
    setSuccessMessage(null);
    try {
      const [file] = e.target.files;
      if (file) {
        const { size } = file;
        if (size / (1024 * 1024) > 2) {
          const sizePretty = prettyBytes(size);
          setErrorMessage({
            ...errorMessage,
            avatar: t('personalData.profile_errors.maxSize', { fileSize: sizePretty, limit: '2 MB' })
          });
          return;
        }

        const picture = URL.createObjectURL(file);
        setUserProfilePicture(picture);
        e.target.value = null;

        if (userProfilePicture) await dispatch(deleteUserProfilePicture());

        const data = new FormData();
        data.append('file', file);

        await dispatch(uploadUserProfilePicture({ data }));
        setSuccessMessage(t('personalData.successMessage'));
        setErrorMessage({
          ...errorMessage,
          avatar: ''
        });
        await dispatch(updateUserProfilePicture());
      }
    } catch (error) {
      setErrorMessage({
        ...errorMessage,
        avatar: t('personalData.errorMessage.generic')
      });
    }
  };

  const deleteUserProfile = async () => {
    await dispatch(deleteUserProfilePicture());
    inputFileRef.current.value = null;
    setUserProfilePicture('');
  };

  const checkErrorMessage = () => {
    return Object.keys(errorMessage).some(key => {
      return errorMessage[key] !== null && errorMessage[key] !== '';
    });
  };

  const role = useMemo(() => {
    if (user && Object.keys(user).length > 0) {
      const { scenario, role, roleOther, roles } = user;
      if (roleOther) return roleOther;
      if (role) return t(`personalData.roles.${role}`);
      if (scenario) return t(`personalData.scenario.${scenario}`);
      if (roles && roles.length > 0) return roles[0];
      return t('personalData.roles.default');
    }
    return '';
  }, [user, t]);

  const profileCompletion = useMemo(() => {
    const totalFields = PROFILE_COMPLETION_FIELDS.length;
    const completedFields = PROFILE_COMPLETION_FIELDS.reduce((count, getter) => {
      const value = getter(userData);
      return count + (isFilledField(value) ? 1 : 0);
    }, 0);

    return Math.round((completedFields / totalFields) * 100);
  }, [userData]);

  return (
    <section className="main_content profile">
      <Breadcrumb data={breadcrumbData} additionClassName="container-narrow" />
      <div className="container container-narrow">
        {checkErrorMessage(errorMessage) ? (
          <div className="errorMessage">
            <span>{t('personalData.errorMessage.overall')}</span>
          </div>
        ) : (
          successMessage && (
            <div className="successMessage">
              <span>{successMessage}</span>
            </div>
          )
        )}
        {/* <h4 className="page_subtitle">{t('profile.subTitle')}</h4> */}
        <div className="personal-data-container personal-data">
          <h2 className="page_title">{t('personalData.title')}</h2>
          <div className="personal-data__progress">
            <div className="personal-data__progress-head">
              <span className="personal-data__progress-title">
                {t('personalData.progress.complete', { percent: profileCompletion })}
              </span>
              <button type="button" className="personal-data__progress-action" onClick={scrollToForm}>
                {t('personalData.progress.completeAction')}
              </button>
            </div>
            <div className="personal-data__progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={profileCompletion}>
              <div className="personal-data__progress-value" style={{ width: `${profileCompletion}%` }} />
            </div>
          </div>
          {/* Profile Picture */}
          <Grid container className="personal-data__card">
            <h4 className="sub_head">{t('personalData.profilepic')}</h4>
            <Grid container className="personal-data__profile-img">
              <Grid className="profile_pic">
                <Avatar
                  loading={userLoading || userProfile.pictureLoading}
                  srcImg={userProfilePicture}
                  name={`${userData?.firstName} ${userData?.lastName}`}
                />
              </Grid>
              <Grid className="profile_pic_right">
                {(userLoading || userProfilePicture) && (
                  <Grid className="profile-picture-name">{t('personalData.profilePictureFileName')}</Grid>
                )}
                <Grid container className="personal-data__profile-img-btns">
                  {(userLoading || userProfilePicture) && (
                    <Grid item sx={6} className="personal-data__btn-col">
                      <Button
                        noArrow
                        borderOnly
                        secondary
                        label={t('personalData.deleteBtn')}
                        onClick={deleteUserProfile}
                        disabled={userLoading || !userProfilePicture}
                      />
                    </Grid>
                  )}
                  <Grid item sx={6} className="personal-data__btn-col">
                    <input
                      ref={inputFileRef}
                      type="file"
                      accept=".jpg, .jpeg, .png"
                      disabled={userLoading}
                      onChange={onChangeProFilePicture}
                      id="profilePictureUploadBtn"
                      className="personal-data__file-input"
                    />
                    <label
                      htmlFor="profilePictureUploadBtn"
                      className={`green-button upload-btn upload-btn--${i18n.language}`}
                    >
                      {userProfilePicture ? t('personalData.replaceBtn') : t('personalData.uploadBtn')}
                    </label>
                  </Grid>
                </Grid>
                {errorMessage.avatar && <Grid className="page_error">{errorMessage.avatar}</Grid>}
              </Grid>
            </Grid>
          </Grid>
          {/* Profile Data */}
          <Grid className="personal-data__card" ref={profileFormRef}>
            <h4 className="sub_head">{t('personalData.title')}</h4>
            <Grid className="personal-data__card-head">
              <Grid>
                <FormControl className="personal-data__form-control">
                  <InputLabel id="salutation">{t('personalData.salutation')}</InputLabel>
                  <Select
                    labelId="salutation"
                    name="salutation"
                    id="salutation"
                    value={userData?.salutation || ''}
                    onChange={setData}
                  >
                    {salutationValues.map(s => (
                      <MenuItem key={s.id} value={s.id}>
                        {t(`personalData.salutationOptions.${s.id}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.firstName')}
                  placeholder={t('personalData.firstName')}
                  value={userData?.firstName || ''}
                  fullWidth
                  className={clsx(
                    'personal-data__text-field',
                    errorMessage.firstName && 'personal-data__text-field--error'
                  )}
                  onChange={setData}
                  name="firstName"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                {errorMessage.firstName && <div className="errorMessageInput">{errorMessage.firstName}</div>}
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.Surname')}
                  placeholder={t('personalData.Surname')}
                  fullWidth
                  value={userData?.lastName || ''}
                  className={clsx(
                    'personal-data__text-field',
                    errorMessage.lastName && 'personal-data__text-field--error'
                  )}
                  onChange={setData}
                  name="lastName"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                {errorMessage.lastName && <div className="errorMessageInput">{errorMessage.lastName}</div>}
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.position')}
                  placeholder={t('personalData.position')}
                  fullWidth
                  value={role}
                  className="personal-data__text-field"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.company')}
                  placeholder={t('personalData.company')}
                  fullWidth
                  className={clsx(
                    'personal-data__text-field',
                    errorMessage.company && 'personal-data__text-field--error'
                  )}
                  margin="normal"
                  value={userData?.company || ''}
                  onChange={setData}
                  name="company"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                {errorMessage.company && <div className="errorMessageInput">{errorMessage.company}</div>}
              </Grid>
            </Grid>
          </Grid>
          {/* Company Address */}
          <Grid className="personal-data__card">
            <h4 className="sub_head">{t('personalData.companyaddress')}</h4>
            <Grid container className="personal-data__card-head">
              <Grid item xs={12}>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.Street')}
                  placeholder={t('personalData.Street')}
                  fullWidth
                  className={clsx(
                    'personal-data__text-field',
                    errorMessage.address && 'personal-data__text-field--error'
                  )}
                  margin="normal"
                  value={(userData && userData.address)?.street || ''}
                  onChange={setAddress}
                  name="street"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.Postcode')}
                  placeholder={t('personalData.Postcode')}
                  className={clsx(
                    'personal-data__text-field',
                    errorMessage.address && 'personal-data__text-field--error'
                  )}
                  value={(userData && userData.address)?.postalCode || ''}
                  onChange={setAddress}
                  name="postalCode"
                  margin="normal"
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.city')}
                  placeholder={t('personalData.city')}
                  className={clsx(
                    'personal-data__text-field',
                    errorMessage.address && 'personal-data__text-field--error'
                  )}
                  margin="normal"
                  fullWidth
                  value={(userData && userData.address)?.city || ''}
                  onChange={setAddress}
                  name="city"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.country')}
                  placeholder={t('personalData.country')}
                  className="personal-data__text-field"
                  margin="normal"
                  fullWidth
                  value={userData?.address?.country ? countriesMap.get(userData.address.country) : ''}
                  onChange={setAddress}
                  name="country"
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                {errorMessage.address && <div className="errorMessageInput">{errorMessage.address}</div>}
              </Grid>
            </Grid>
          </Grid>
          {/* Contact */}
          <Grid className="personal-data__card personal-data__card--contact">
            <h4 className="sub_head">{t('personalData.Contact_Information')}</h4>
            <Grid className="personal-data__card-head">
              <TextField
                id="standard-full-width"
                label={t('personalData.phone')}
                placeholder={t('personalData.phone')}
                className={clsx(
                  'personal-data__text-field',
                  errorMessage.phoneNumber && 'personal-data__text-field--error'
                )}
                value={(userData && userData.address)?.phoneNumber || ''}
                onChange={setAddress}
                name="phoneNumber"
                margin="normal"
                fullWidth
                InputLabelProps={{
                  shrink: true
                }}
              />
              <TextField
                id="standard-full-width"
                label={t('personalData.Faxnumber')}
                placeholder={t('personalData.Faxnumber')}
                className="personal-data__text-field"
                value={(userData && userData.address)?.faxNumber || ''}
                onChange={setAddress}
                name="faxNumber"
                margin="normal"
                fullWidth
                InputLabelProps={{
                  shrink: true
                }}
              />
              {errorMessage.phoneNumber && <Grid className="errorMessageInput">{errorMessage.phoneNumber}</Grid>}
            </Grid>
          </Grid>
          {/* Address */}
          <Grid className="personal-data__card personal-data__card--address">
            <h4 className="sub_head">{t('personalData.Additional_Information')}</h4>
            <Grid className="personal-data__card-head">
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.VAT_Number')}
                  placeholder={t('personalData.VAT_Number')}
                  fullWidth
                  value={userData?.vatNumber || ''}
                  onChange={setData}
                  name="vatNumber"
                  className="personal-data__text-field"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.TCO_Number')}
                  placeholder={t('personalData.TCO_Number')}
                  fullWidth
                  value={userData?.sealNumber || ''}
                  onChange={setData}
                  name="sealNumber"
                  className="personal-data__text-field"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.Diesel_repair_service_id')}
                  placeholder={t('personalData.Diesel_repair_service_id')}
                  fullWidth
                  className="personal-data__text-field"
                  value={userData?.drsId || ''}
                  onChange={setData}
                  name="drsId"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.ATE_Break_Center_ID')}
                  placeholder={t('personalData.ATE_Break_Center_ID')}
                  fullWidth
                  value={userData?.brakeCenterId || ''}
                  onChange={setData}
                  name="brakeCenterId"
                  className="personal-data__text-field"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.VDO_Fleet_Dealer_ID')}
                  placeholder={t('personalData.VDO_Fleet_Dealer_ID')}
                  fullWidth
                  value={userData?.dealerId || ''}
                  onChange={setData}
                  name="dealerId"
                  className="personal-data__text-field"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid className="personal-data__card personal-data__card--newsletter">
            <h4 className="sub_head">{t('personalData.newsletterTitle')}</h4>
            <Grid className="personal-data__card-head">
              <Checkbox
                id="newsletter"
                className="personal-data__checkbox"
                name="newsletter"
                checked={userData && userData.hasOwnProperty('newsletter') ? userData.newsletter : false}
                onChange={setCheck}
              />{' '}
              <label htmlFor="newsletter" className="checkbox-text">
                {t('personalData.newsletter')}
              </label>
            </Grid>
          </Grid>
          <Grid>
            <div className="personal-data__footer-btn">
              <Button onClick={goBack} ghost noArrow label={t('personalData.Abortbtn')} />
              <Button onClick={saveProfileData} label={t('personalData.saveBtn')} />
            </div>
          </Grid>
        </div>
      </div>
    </section>
  );
};

export { PersonalData };
